import { cn } from '@/lib/ui/style-helpers'

import { Badge } from '@/components/lemmala-design-system/badge/badge'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'

import { AvatarWithTooltip } from './avatar-with-tooltip'
import { AVATAR_OVERFLOW_BADGE_SIZE } from './consts'
import { getAvatarOverflowLabel } from './labels'
import { StringNameAvatar } from './string-name-avatar'
import { type AvatarListSize } from './types'
import {
  avatarChipVariants,
  avatarListVariants,
  avatarOverflowBadgeVariants,
  avatarOverlapVariants,
} from './variants'

type AvatarListProps = {
  labels: string[]
  size?: AvatarListSize
  maxVisible?: number
  className?: string
}

export const AvatarList = ({
  labels,
  size = 'small',
  maxVisible = 3,
  className,
}: AvatarListProps) => {
  const visibleLabels = labels.slice(0, maxVisible)
  const remainingLabels = labels.slice(maxVisible)

  return (
    <div className={cn(avatarListVariants({ size }), className)} data-testid='avatar-list'>
      <div className='flex items-center'>
        {visibleLabels.map((label) => (
          <div key={label} className={avatarOverlapVariants({ size })}>
            <AvatarWithTooltip size={size} name={label} />
          </div>
        ))}
      </div>

      {remainingLabels.length > 0 && (
        <Tooltip
          trigger={
            <Badge
              data-testid='avatar-list-overflow'
              aria-label={getAvatarOverflowLabel(remainingLabels.length)}
              variant='neutral'
              badgeStyle='ghost'
              size={AVATAR_OVERFLOW_BADGE_SIZE[size]}
              className={avatarOverflowBadgeVariants({ size })}
            >
              +{remainingLabels.length}
            </Badge>
          }
        >
          <div className='flex flex-col gap-1' data-testid='avatar-list-overflow-content'>
            {remainingLabels.map((label) => (
              <div key={label} className={cn(avatarChipVariants(), 'w-full min-w-0')}>
                <StringNameAvatar name={label} size='extra-small' />
                <span className='min-w-0 truncate text-body-small'>{label}</span>
              </div>
            ))}
          </div>
        </Tooltip>
      )}
    </div>
  )
}
